import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import InfoBubble from "../components/InfoBubble";
import useGuidance from "../hooks/useGuidance";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  TICKET_STATUS_STYLES as STATUS_STYLES,
  TICKET_PRIORITY_STYLES as PRIORITY_STYLES,
  TICKET_CATEGORY_LABELS as CATEGORY_LABELS,
} from "../constants/registry";
import {
  Ticket, Plus, Loader2, MessageSquare, Send, Clock,
  CheckCircle, ChevronRight, ArrowLeft, X
} from "lucide-react";

const MySupportPage = () => {
  const { user: authUser } = useAuth();
  const { guidance } = useGuidance();
  const { t } = useTranslation('platform');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category: "general", priority: "medium" });
  const [msgText, setMsgText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/crm/support-tickets`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        // API returns {tickets: [...], total: N}
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error("Tickets fetch failed:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const loadDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/crm/support-tickets/${ticketId}`, { credentials: "include" });
      if (res.ok) setSelectedTicket(await res.json());
    } catch (err) { toast.error("Failed to load ticket"); }
    setDetailLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/crm/support-tickets`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ ...form, channel: "manual" }),
      });
      if (res.ok) {
        toast.success("Support request submitted");
        setShowCreate(false);
        setForm({ subject: "", description: "", category: "general", priority: "medium" });
        fetchTickets();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Failed to submit");
      }
    } catch (err) { toast.error("Failed to submit"); }
    setCreating(false);
  };

  const handleReply = async () => {
    if (!msgText.trim() || !selectedTicket) return;
    setSendingMsg(true);
    try {
      const res = await fetch(`${API}/crm/support-tickets/${selectedTicket.ticket_id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ content: msgText, visibility: "external" }),
      });
      if (res.ok) {
        setMsgText("");
        loadDetail(selectedTicket.ticket_id);
      }
    } catch (err) { toast.error("Failed to send reply"); }
    setSendingMsg(false);
  };

  const openCount = tickets.filter(t => !["resolved", "closed"].includes(t.status)).length;

  return (
    <QMSLayout title={t('support.mySupport')}>
      <div className="max-w-4xl mx-auto" data-testid="my-support-page">
        {guidance?.info_hint && <InfoBubble text={guidance.info_hint} title={t('support.mySupport')} />}
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-white font-bold text-xl font-['Syne']">{t('support.myTickets')}</h1>
            <p className="text-[#8B95A5] text-sm mt-1">{openCount} open {openCount === 1 ? "request" : "requests"}</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] transition-colors" data-testid="my-support-create-btn">
            <Plus size={14} /> {t('support.newRequest')}
          </button>
        </div>

        {selectedTicket ? (
          /* Ticket Detail View */
          <div className="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27]" data-testid="my-support-detail">
            <div className="p-4 border-b border-[rgba(255,255,255,0.06)]">
              <button onClick={() => setSelectedTicket(null)} className="flex items-center gap-1 text-[#8B95A5] hover:text-white text-xs mb-3" data-testid="my-support-back-btn">
                <ArrowLeft size={12} /> {t('support.backToTickets')}
              </button>
              <h2 className="text-white font-semibold text-base mb-1" data-testid="my-support-detail-subject">{selectedTicket.subject}</h2>
              <p className="text-[#8B95A5] text-sm mb-3">{selectedTicket.description}</p>
              <div className="flex gap-2 text-[10px]">
                {(() => { const st = STATUS_STYLES[selectedTicket.status]; return <span className={`px-2 py-0.5 rounded font-medium ${st?.bg} ${st?.text}`} data-testid="my-support-detail-status">{st?.label}</span>; })()}
                {(() => { const pr = PRIORITY_STYLES[selectedTicket.priority]; return <span className={`px-2 py-0.5 rounded font-medium ${pr?.bg} ${pr?.text}`}>{selectedTicket.priority}</span>; })()}
                <span className="px-2 py-0.5 rounded bg-[rgba(255,255,255,0.04)] text-[#8B95A5]">{CATEGORY_LABELS[selectedTicket.category]}</span>
                <span className="px-2 py-0.5 rounded bg-[rgba(255,255,255,0.04)] text-[#8B95A5] font-mono">{selectedTicket.ticket_id}</span>
              </div>
            </div>

            {/* Messages (external only — enforced by backend) */}
            <div className="p-4 space-y-3 min-h-[200px]" data-testid="my-support-messages">
              {detailLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#36A9E1]" size={20} /></div>
              ) : (selectedTicket.messages || []).length === 0 ? (
                <div className="text-center text-[#8B95A5] text-xs py-8">{t('support.noMessages')}</div>
              ) : (
                (selectedTicket.messages || []).map(msg => (
                  <div key={msg.message_id} className={`p-3 rounded-lg text-xs ${msg.actor_type === "internal" ? "bg-[rgba(54,169,225,0.06)] border border-[rgba(54,169,225,0.1)]" : "bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-medium">{msg.actor_type === "internal" ? "Support Team" : msg.actor_email}</span>
                      <span className="text-[#8B95A5] text-[9px] ml-auto">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-[#B8C7D9] whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))
              )}
            </div>

            {/* Reply */}
            {!["resolved", "closed"].includes(selectedTicket.status) && (
              <div className="p-4 border-t border-[rgba(255,255,255,0.06)]">
                <div className="flex gap-2">
                  <input
                    value={msgText} onChange={e => setMsgText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleReply()}
                    placeholder={t('support.typeMessage')}
                    className="flex-1 px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
                    data-testid="my-support-msg-input"
                  />
                  <button onClick={handleReply} disabled={sendingMsg || !msgText.trim()} className="px-3 py-2 rounded-md bg-[#36A9E1] text-white disabled:opacity-50" data-testid="my-support-msg-send">
                    {sendingMsg ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Ticket List */
          loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-16 text-[#8B95A5]">
              <Ticket size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm mb-1">{t('support.noTickets')}</p>
              <p className="text-xs">{t('support.noTicketsHint')}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="my-support-ticket-list">
              {tickets.map(tkt => {
                const st = STATUS_STYLES[tkt.status] || STATUS_STYLES.open;
                const pr = PRIORITY_STYLES[tkt.priority] || PRIORITY_STYLES.medium;
                return (
                  <button
                    key={tkt.ticket_id}
                    onClick={() => loadDetail(tkt.ticket_id)}
                    className="w-full text-left p-4 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] hover:border-[rgba(255,255,255,0.15)] transition-colors"
                    data-testid={`my-support-ticket-${tkt.ticket_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white text-sm font-medium truncate">{tkt.subject}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${pr.bg} ${pr.text}`}>{tkt.priority}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-[#8B95A5]">
                          <span>{CATEGORY_LABELS[tkt.category]}</span>
                          <span className="font-mono">{tkt.ticket_id}</span>
                          {tkt.message_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare size={8} />{tkt.message_count} replies</span>}
                          <span>{new Date(tkt.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-[#8B95A5] shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          )
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="my-support-create-modal">
            <form onSubmit={handleCreate} className="bg-[#1A1D27] border border-[rgba(255,255,255,0.1)] rounded-lg p-6 w-full max-w-lg shadow-2xl">
              <h2 className="text-white font-semibold text-lg mb-4 font-['Syne']">{t('support.newRequest')}</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">{t('support.subject')} *</label>
                  <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none" required data-testid="my-support-form-subject" />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">{t('support.description')} *</label>
                  <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={4} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none resize-none" required data-testid="my-support-form-description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">{t('support.category')}</label>
                    <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-xs" data-testid="my-support-form-category">
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">{t('support.priority')}</label>
                    <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="w-full px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-xs" data-testid="my-support-form-priority">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-md text-sm text-[#8B95A5] hover:text-white transition-colors">Cancel</button>
                <button type="submit" disabled={creating} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] disabled:opacity-50 transition-colors" data-testid="my-support-form-submit">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {t('support.submitRequest')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default MySupportPage;
